import { FlexBox } from '@airship-platform/jam-web/components';
import { MailIcon, SmsIcon } from '@airship-platform/jam-web/icons';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { ContactInfo } from '../../../../data-access/appointments';

export const SendSurveyFormSchema = z
  .object({
    customContact: z.union([z.email(), z.literal('')]),
    surveyContacts: z
      .object({
        value: z.email(),
        type: z.enum(['Email', 'MobilePhone', 'Phone', 'Fax']),
      })
      .array(),
  })
  .superRefine((data, ctx) => {
    const contacts = data.surveyContacts
      .concat({
        type: 'Email',
        value: data.customContact.trim(),
      })
      .filter((d) => d.value);

    if (contacts.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please select at least one contact or enter a cutomer email',
      });
    }
  });

export type SendSurveyFormInputs = z.infer<typeof SendSurveyFormSchema>;

interface SendSurveySectionProps {
  contacts: ContactInfo[];
}

const SendSurveyForm: React.FC<SendSurveySectionProps> = ({ contacts }) => {
  const { control, setValue, getValues, watch, formState } =
    useFormContext<SendSurveyFormInputs>();

  const setContacts = (contact: ContactInfo, checked: boolean) => {
    const surveyContacts = getValues('surveyContacts');
    if (checked) {
      setValue('surveyContacts', [...surveyContacts, contact]);
    } else {
      setValue(
        'surveyContacts',
        surveyContacts.filter((c) => c.value !== contact.value)
      );
    }
  };

  const surveyContacts = watch('surveyContacts');

  return (
    <FlexBox flexDirection={'column'} gap={3} width={'100%'}>
      <Box component={FormGroup} sx={{ display: 'flex', gap: 2 }}>
        {contacts
          .filter((contact) => ['email'].includes(contact.type.toLowerCase()))
          .sort((a, b) => a.value.localeCompare(b.value))
          .map((contact, index) => (
            <FormControlLabel
              key={index}
              checked={surveyContacts.some((e) => e.value === contact.value)}
              sx={(theme) => ({
                backgroundColor: surveyContacts.some(
                  (d) => d.value === contact.value
                )
                  ? theme.palette.gold?.[200]
                  : theme.palette.softGold?.[50],
                margin: 0,
                paddingX: theme.spacing(2),
                paddingY: theme.spacing(1),
                borderRadius: theme.shape.borderRadius,
                width: '100%',
                textAlign: 'center',
              })}
              labelPlacement="top"
              control={
                <Checkbox
                  onChange={(e) => setContacts(contact, e.target.checked)}
                  sx={{ display: 'none' }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    component={'div'}
                    color="secondary.dark"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      fontWeight: 500,
                      gap: 1,
                    }}
                  >
                    {contact.type === 'MobilePhone' ? (
                      <SmsIcon />
                    ) : (
                      <MailIcon />
                    )}
                    {contact.value}
                  </Typography>
                </Box>
              }
            />
          ))}
      </Box>
      <Controller
        control={control}
        name="customContact"
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="Customer email"
            fullWidth
            error={!!error}
            helperText={error?.message}
            onKeyDown={(e) => e.code === 'Enter' && e.preventDefault()}
          />
        )}
      />
      <FlexBox flexDirection={'column'}>
        <Typography variant="body2" color="error.main" sx={{ paddingX: 1 }}>
          {Object.entries(formState.errors)
            .flatMap(([key, val]) => (!key ? [val.message] : []))
            .join('')}
        </Typography>
      </FlexBox>
    </FlexBox>
  );
};

export default SendSurveyForm;
